import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Divider,
    Grid,
    Chip,
    Alert,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import {
    Business as BusinessIcon,
    CardMembership as LicenseIcon,
    Person as PersonIcon,
    Public as DomainIcon,
    Palette as BrandingIcon,
    Storage as DemoIcon,
    Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { OnboardingData } from '../CompanyOnboardingWizard';

interface Props {
    data: OnboardingData;
}

const SummarySection: React.FC<{
    icon: React.ReactNode;
    title: string;
    children: React.ReactNode;
}> = ({ icon, title, children }) => (
    <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {icon}
                <Typography variant="subtitle1" fontWeight="bold">
                    {title}
                </Typography>
            </Box>
            {children}
        </CardContent>
    </Card>
);

const SummaryItem: React.FC<{
    label: string;
    value: string | React.ReactNode;
}> = ({ label, value }) => (
    <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2">{value || '-'}</Typography>
    </Box>
);

export const SummaryStep: React.FC<Props> = ({ data }) => {
    const translate = useTranslate();

    const formatAddress = () => {
        const parts = [
            data.companyStreet,
            [data.companyZip, data.companyCity].filter(Boolean).join(' '),
            data.companyCountry,
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : '-';
    };

    // Cluster-aware wildcard label: Stage-Build → demo-stage, Prod-Build → demo/app.
    const isStageBuild = (process.env.REACT_APP_SSO_URL || '').includes('stage.');
    const wildcardLabel = data.isDemoTenant
        ? (isStageBuild ? 'demo-stage' : 'demo')
        : 'app';
    const fullDomain = data.domainMode === 'subdomain'
        ? `${data.subdomainSlug}.${wildcardLabel}.generationl-onlineacademy.de`
        : data.customDomain;

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {translate('companyOnboarding.steps.summaryTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {translate('companyOnboarding.steps.summaryDescription')}
            </Typography>

            {data.isDemoTenant && (
                <Alert severity="warning" icon={<DemoIcon />} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                        {translate('companyOnboarding.summary.demoMode')}
                    </Typography>
                    <Typography variant="body2">
                        {translate('companyOnboarding.summary.demoExpires', { days: data.demoExpiresInDays })}
                    </Typography>
                </Alert>
            )}

            <SummarySection icon={<BusinessIcon color="primary" />} title={translate('companyOnboarding.summary.companySection')}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <SummaryItem label={translate('companyOnboarding.fields.companyName')} value={data.companyName} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <SummaryItem label={translate('companyOnboarding.fields.companyEmail')} value={data.companyEmail} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <SummaryItem label={translate('companyOnboarding.fields.address')} value={formatAddress()} />
                    </Grid>
                </Grid>
            </SummarySection>

            <SummarySection icon={<DomainIcon color="primary" />} title={translate('companyOnboarding.summary.domainSection')}>
                <SummaryItem
                    label={translate('companyOnboarding.fields.domain')}
                    value={<code>{fullDomain}</code>}
                />
                {data.domainMode === 'custom' && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {data.customDomain}. CNAME app.generationl-onlineacademy.de.
                        </Typography>
                    </Alert>
                )}
            </SummarySection>

            <SummarySection icon={<BrandingIcon color="primary" />} title={translate('companyOnboarding.summary.brandingSection')}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <SummaryItem label={translate('companyOnboarding.fields.appTitle')} value={data.appTitle} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <SummaryItem
                            label={translate('companyOnboarding.fields.primaryColor')}
                            value={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 16, height: 16, bgcolor: data.primaryColor, borderRadius: 0.5, border: '1px solid #ccc' }} />
                                    <span>{data.primaryColor}</span>
                                </Box>
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <SummaryItem
                            label={translate('companyOnboarding.fields.secondaryColor')}
                            value={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 16, height: 16, bgcolor: data.secondaryColor, borderRadius: 0.5, border: '1px solid #ccc' }} />
                                    <span>{data.secondaryColor}</span>
                                </Box>
                            }
                        />
                    </Grid>
                </Grid>
            </SummarySection>

            {!data.isDemoTenant && (
                <SummarySection icon={<LicenseIcon color="primary" />} title={translate('companyOnboarding.summary.licenseSection')}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <SummaryItem
                                label={translate('companyOnboarding.fields.product')}
                                value={data.productName ? <Chip label={data.productName} size="small" /> : '-'}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <SummaryItem
                                label={translate('companyOnboarding.fields.licenseExpiresAt')}
                                value={data.licenseExpiresAt ? new Date(data.licenseExpiresAt).toLocaleDateString('de-DE') : translate('companyOnboarding.summary.unlimited')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <SummaryItem
                                label={translate('companyOnboarding.fields.maxUsers')}
                                value={data.maxUsers || translate('companyOnboarding.summary.unlimited')}
                            />
                        </Grid>
                    </Grid>
                </SummarySection>
            )}

            <SummarySection icon={<PersonIcon color="primary" />} title={translate('companyOnboarding.summary.adminsSection')}>
                <List dense disablePadding>
                    {data.admins.map((a, i) => (
                        <ListItem key={i} disableGutters>
                            <ListItemText
                                primary={a.email}
                                secondary={
                                    [a.firstName, a.lastName].filter(Boolean).join(' ') +
                                    (a.role && a.role !== 'ROLE_COMPANY_ADMIN' ? ` · ${a.role}` : '')
                                }
                            />
                        </ListItem>
                    ))}
                </List>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary">
                    {translate('companyOnboarding.summary.adminInvitationNote')}
                </Typography>
            </SummarySection>

            {data.isDemoTenant && (
                <SummarySection icon={<DemoIcon color="warning" />} title={translate('companyOnboarding.summary.demoDataSection')}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <SummaryItem
                                label={translate('companyOnboarding.fields.demoSourceCompany')}
                                value={data.demoSourceCompanyId ? `#${data.demoSourceCompanyId}` : '-'}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <SummaryItem
                                label={translate('companyOnboarding.fields.demoExpiresInDays')}
                                value={`${data.demoExpiresInDays} ${translate('companyOnboarding.units.days')}`}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="caption" color="text.secondary">
                                {translate('companyOnboarding.fields.demoSelectionTitle')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                {Object.entries(data.demoSelection)
                                    .filter(([, v]) => v)
                                    .map(([k]) => (
                                        <Chip
                                            key={k}
                                            label={translate(`companyOnboarding.fields.demoSelection.${k}`)}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))}
                            </Box>
                        </Grid>
                    </Grid>
                </SummarySection>
            )}
        </Box>
    );
};
